import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { CarouselControls } from "./carousel-controls";
import { StateController } from "../../state";
import type { Screenshot } from "../package";

const KEYCODE_ESCAPE = "Escape";
const KEYCODE_LEFT = "ArrowLeft";
const KEYCODE_RIGHT = "ArrowRight";
const KEYCODE_F = "f";

@customElement("umb-market-carousel")
export class UmbMarketCarousel extends LitElement {
  @property({ type: Array })
  screenshots: Array<Screenshot> = [];

  @property({ type: String })
  videoUrl?: string;

  @state()
  private _carouselControls!: CarouselControls;

  @state()
  private _fullscreen = false;

  private _fullscreenTriggerIndex?: number;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("keydown", this.#handleFocus);
    window.addEventListener("resize", this.#handleResize);

    this.#setCarouselControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this.#handleFocus);
    window.removeEventListener("resize", this.#handleResize);
  }

  #handleResize = () => {
    this._carouselControls.resize();
  };

  #handleFocus = (e: KeyboardEvent) => {
    const carouselControlsFocused =
      StateController.lastFocusedElement.hasAttribute("data-action");

    if (e.key === KEYCODE_ESCAPE) {
      this.#setFullscreen(false);
    } else if (e.key === KEYCODE_LEFT) {
      this._carouselControls.slide("prev");
    } else if (e.key === KEYCODE_RIGHT) {
      this._carouselControls.slide("next");
    } else if (e.key === KEYCODE_F && carouselControlsFocused) {
      this.#setFullscreen(true);
    }
  };

  #setCarouselControls(activeIndex: number = 0) {
    const maxPerView = this._fullscreen ? 1 : 2;

    this._carouselControls = new CarouselControls(
      this,
      this.screenshots.length,
      maxPerView,
      "carousel",
      activeIndex
    );
  }

  #setFullscreen(fullscreen: boolean, activeIndex?: number) {
    this._fullscreen = fullscreen;

    if (fullscreen) {
      this.classList.add("fullscreen");
    } else {
      this.classList.remove("fullscreen");
    }

    this.#setCarouselControls(activeIndex ?? this._fullscreenTriggerIndex);
    this._fullscreenTriggerIndex = activeIndex;
  }

  render() {
    return html`
      <div>
        ${when(
          this._fullscreen,
          () =>
            html`<div id="header">
              <umb-market-button
                id="closeBtn"
                @click=${() => this.#setFullscreen(false)}
              >
                Close
              </umb-market-button>
            </div>`
        )}
        <div
          id="carousel"
          class="${this._carouselControls?.itemCount <=
          this._carouselControls?.cardsPerView
            ? "centered"
            : ""}"
        >
          ${when(
            this.videoUrl,
            () =>
              html`<umb-market-carousel-card
                .model=${{
                  deliveryPath: this.videoUrl!,
                  sourceUrl: this.videoUrl!,
                }}
                ?fullscreen=${this._fullscreen}
              ></umb-market-carousel-card>`
          )}
          ${this.screenshots.map(
            (screenshot, i) => html`<umb-market-carousel-card
              .model=${screenshot}
              ?fullscreen=${this._fullscreen}
              @expand=${() => {
                this.#setFullscreen(true, i);
              }}
            ></umb-market-carousel-card>`
          )}
        </div>
        <umb-market-card-deck-header
          .controls=${this._carouselControls}
          .static=${false}
          ><umb-market-button
            @click=${() => this.#setFullscreen(true)}
            id="expandBtn"
            data-action="expand"
          >
            Expand
          </umb-market-button></umb-market-card-deck-header
        >
      </div>
    `;
  }

  static styles = [
    css`
      :host {
        --button-align: 0;
        --button-padding: 0 var(--s-spacer);
        --button-height: var(--btn-round-size);
        --header-margin-bottom: 0;
        --button-background: var(--dominant-color, white);
        --button-fill-color: var(--dominant-color, white);
        --button-color: var(--dominant-color-text, black);
        --button-hover-color: var(--dominant-color-text, black);
        --fill: var(--dominant-color-text, black);
        --button-border-radius: 50%;
        --carousel-gap: var(--l-spacer);
      }

      :host(:not(.fullscreen)) {
        width: 100%;
      }

      :host(:not(.fullscreen)) > div {
        padding-bottom: var(--l-spacer);
        position: relative;
        z-index: 2;
        max-width: var(--max-width);
      }

      :host(:not(.fullscreen)) > div::after,
      :host(:not(.fullscreen)) > div::before {
        content: "";
        position: absolute;
        width: 100vw;
        top: var(--xxl-spacer);
        bottom: 0;
        left: 50%;
        transform: translateX(-100%);
        background: var(--dominant-color-header, rgba(var(--blueRgb), 0.33));
        opacity: 0.75;
        z-index: -1;
      }

      :host(:not(.fullscreen)) > div::after {
        transform: translateX(0);
      }

      #carousel {
        display: flex;
        transition: all 300ms ease;
        position: relative;
        gap: var(--carousel-gap);
      }

      #expandBtn,
      #closeBtn {
        --button-width: auto;
        --button-border-radius: 100px;
        --button-color: var(--dominant-color-text);

        align-items: center;
        height: var(--button-height);
        margin-left: var(--s-spacer);
      }

      #expandBtn,
      #expandBtn:hover {
        background: var(--button-fill-color);
      }

      #header {
        display: flex;
        justify-content: flex-end;
        margin-bottom: var(--s-spacer);
      }

      :host(.fullscreen) #expandBtn {
        display: none;
      }

      .centered {
        justify-content: center;
      }

      umb-market-card-deck-header {
        z-index: 5050;
        justify-content: center;
        margin-top: var(--xs-spacer);
      }

      :host(.fullscreen) {
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        position: fixed;
        margin: 0 !important;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 6060;
        padding: var(--xs-spacer);
        order: 0;

        --carousel-card-width: 100%;
        --carousel-card-transform: none;
        --carousel-gap: var(--xs-spacer);
      }

      :host(.fullscreen)::after {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        background: var(--dominant-color-header, var(--uui-color-default));
        opacity: 0.75;
        z-index: -1;
      }

      :host(.fullscreen) > div {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
      }

      .close-btn {
        border-radius: 100px;
        padding: 12px 24px;
        align-self: flex-end;
        margin-bottom: var(--xs-spacer);
      }

      @media (min-width: 767px) {
        :host {
          --carousel-gap: var(--l-spacer);
        }

        #carousel {
          flex-direction: row;
        }

        :host(.fullscreen) {
          --carousel-gap: var(--xl-spacer);
          padding: var(--xl-spacer);
        }

        :host(.fullscreen) button {
          margin: 0 17px 0 auto;
          transform: translateY(-50%);
        }

        umb-market-card-deck-header {
          margin-top: var(--s-spacer);
          justify-content: flex-start;
        }
      }
    `,
  ];
}
